import { FC, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateBooking } from "@1-now/booking-core";
import { trackBookingStarted } from "analytics/bookingEvents";
import { uploadCustomerFiles } from "api/customer/customer";
import { useData } from "data/data-provider";
import { setRenterSession } from "utils/renterSession";
import CarPhoto from "../BookingSteps/CarPhoto";
import { payLaterPoliciesState } from "../PayLaterPoliciesPage/PayLaterPoliciesPage";
import EmailCodeBlock from "../EmailCode/EmailCodeBlock";
import { VerifyOutcome, verifyEmailCode } from "../EmailCode/emailCode";
import { HeldBooking, clearHeld, takeHeldFiles } from "../EmailCode/heldBooking";

/**
 * "Check your email" on the Reserved page (1NOW-10770): where a returning renter lands after "Pay later", with the
 * reservation held, not made (HeldBooking). The code only proves the email: a right one makes the booking from the
 * held payload, once, and from there it is a new renter's pay-later path, the policies page with that booking's own
 * filled-in agreement (as the car pages open it), then the same reserved landing.
 * "Use a different email" drops the hold and goes back to the car page's Details with the email emptied; no
 * booking was made.
 */
const ReservedCodeGate: FC<{ held: HeldBooking }> = ({ held }) => {
  const navigate = useNavigate();
  const { ourCompany, setBookingdetails, setEmail } = useData();
  const createBooking = useCreateBooking();
  /** The renter whose code was right: a booking that then fails is tried again without the spent code. */
  const verified = useRef<any>(null);

  const onVerify = async (code: string): Promise<VerifyOutcome> => {
    if (!verified.current) {
      const r = await verifyEmailCode(held.customer, code);
      if ("error" in r) return { error: r.error, bad: true };
      setRenterSession(r.data);
      verified.current = r.data;
    }
    const user = verified.current;
    const files = takeHeldFiles();
    if (held.proofRequired && files.length === 0)
      return { error: "Your insurance document was cleared when the page reloaded. Go back to the car and add it again." };
    const made = await createBooking({ ...held.payload, customer: user.id });
    if (made.error) return { error: made.error };
    const booking = made.booking;
    for (const { file } of files) await uploadCustomerFiles(String(user.unique_identifier), [file], "insurance");
    setBookingdetails(booking);
    // Pay later never opens the checkout, where "started booking" is counted: counted here, once it exists (1NOW-11119).
    trackBookingStarted(booking, held.car, ourCompany?.currency);
    clearHeld();
    // Replaced, so Back does not come back to a code box with nothing held behind it.
    navigate("/pay-later-policies", { replace: true, state: await payLaterPoliciesState(booking.unique_identifier) });
    return { ok: true };
  };

  const onDifferentEmail = () => {
    clearHeld();
    setEmail("");
    // Replaced, so Back from the car page does not come back to a code box with nothing behind it.
    navigate(held.returnTo, { replace: true, state: { differentEmail: true } });
  };

  return (
    <div className="container mx-auto max-w-md px-4 pb-12 pt-8 text-text-color">
      <h1 className="mb-4 text-2xl font-extrabold uppercase leading-tight">Check your email</h1>
      <div className="flex items-center gap-3 rounded-2xl border border-secondary-100 bg-card-color p-3">
        <CarPhoto photo={held.car.photo} className="h-12 w-[72px] rounded-lg" />
        <div className="min-w-0">
          <b className="block truncate">{held.car.name}</b>
          <span className="block text-xs opacity-70">{held.car.range}</span>
        </div>
      </div>
      <div className="mt-3 rounded-2xl border border-secondary-100 bg-card-color p-4">
        <EmailCodeBlock
          heading={false}
          customer={held.customer}
          resendAfter={held.resendAt ? Math.max(0, Math.ceil((held.resendAt - Date.now()) / 1000)) : 0}
          error={held.error}
          onVerify={onVerify}
          onDifferentEmail={onDifferentEmail}
        />
      </div>
      <p className="mt-4 text-xs opacity-70">Your car is not reserved yet. We reserve it as soon as the code is confirmed.</p>
    </div>
  );
};

export default ReservedCodeGate;
